/**
 * Global window error and unhandledrejection listeners with OTel capture.
 *
 * Call `installGlobalErrorListeners()` once from the SPA entry point
 * **after** `initTelemetry()`.  The listeners create short-lived OTel
 * spans for errors that escape React's tree (e.g. errors in event handlers,
 * setTimeout callbacks, or unhandled promise rejections).
 *
 * ## PII policy
 *
 * Only `exception.type` and `exception.message` are recorded.
 * Stack traces, raw URLs, and user data are intentionally omitted.
 *
 * ## Browser logging
 *
 * The handlers do **not** call `event.preventDefault()`, so the browser's
 * default error logging (console + DevTools) remains unaffected.
 *
 * @module E3 – Browser error capture (global listeners)
 */

import { recordErrorSpan } from "@/components/error-boundary";

// ---------------------------------------------------------------------------
// Module state
// ---------------------------------------------------------------------------

let installed = false;

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Register `window.error` and `window.unhandledrejection` listeners.
 *
 * Safe to call multiple times — subsequent calls are no-ops.
 */
export function installGlobalErrorListeners(): void {
  if (installed || typeof window === "undefined") {
    return;
  }
  installed = true;

  // ---- window.error -------------------------------------------------------
  // Fires for uncaught runtime errors (e.g. thrown in setTimeout, event handlers).
  window.addEventListener("error", (event: ErrorEvent) => {
    const error = event.error ?? new Error(event.message || "Unknown error");
    recordErrorSpan("ui.window_error", error, {
      "error.source": "window.onerror",
      // `event.filename` could contain the source file URL — record only the
      // bare filename (no path/query) to avoid leaking build paths or tokens.
      ...(event.filename
        ? { "error.filename": fileBasename(event.filename) }
        : {}),
      ...(event.lineno ? { "error.lineno": event.lineno } : {}),
      ...(event.colno ? { "error.colno": event.colno } : {}),
    });

    // Do NOT preventDefault — let the browser log normally.
  });

  // ---- window.unhandledrejection ------------------------------------------
  // Fires for Promise rejections that have no .catch() handler.
  window.addEventListener(
    "unhandledrejection",
    (event: PromiseRejectionEvent) => {
      const reason = event.reason;
      recordErrorSpan("ui.unhandled_rejection", reason, {
        "error.source": "window.onunhandledrejection",
      });

      // Do NOT preventDefault — let the browser log normally.
    },
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Extract the filename portion of a URL/path, stripping directories and
 * query params to avoid PII leakage.
 *
 * Examples:
 *   "https://example.com/assets/index-abc123.js?v=1" → "index-abc123.js"
 *   "/home/user/project/src/main.tsx"                → "main.tsx"
 */
function fileBasename(urlOrPath: string): string {
  try {
    // Try to parse as URL first (handles "https://host/path?query" case).
    const url = new URL(urlOrPath, "https://localhost");
    const segments = url.pathname.split("/");
    return segments[segments.length - 1] || urlOrPath;
  } catch {
    // Fallback: simple path split.
    const segments = urlOrPath.split("/");
    return segments[segments.length - 1] || urlOrPath;
  }
}
